import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxCopytextModule } from '@aposin/ng-aquila/copytext';
import { NxFileUploaderModule } from '@aposin/ng-aquila/file-uploader';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxLinkModule } from '@aposin/ng-aquila/link';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { NX_MODAL_DATA, NxDialogService } from '@aposin/ng-aquila/modal';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { TranslateModule } from '@ngx-translate/core';

import { DocumentListComponent } from '#components/document-list/document-list.component';
import { DocumentHandler } from '#components/DocumentHandler';
import { FileUploaderComponent } from '#components/file-uploader/file-uploader.component';
import { ConfirmationModalComponent } from '#modals/confirmation-modal';
import { displayError } from '#modals/error-modal';
import { UpdateModalSubmitParams } from '#modals/update-modal/update-modal.component';
import CveService from '#services/cve/cve.service';
import { ReputationalRiskData } from '#services/cve/VettingIteration';
import DocumentService from '#services/document/document.service';
import UserService from '#services/user/user.service';
import { REP_RISK_SHARE_POINT_LINK } from '#utils/constants';
import { formatDate } from '#utils/dateUtils';

const MIN_SCORE_TO_ESCALATE = 3;

@Component({
  templateUrl: './reputational-risk-update-modal.component.html',
  imports: [
    NxCopytextModule,
    NxHeadlineModule,
    NxFormfieldModule,
    FormsModule,
    NxInputModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxRadioToggleModule,
    NxRadioModule,
    NxMessageModule,
    NxLinkModule,
    NxIconModule,
    NxSpinnerModule,
    NxFileUploaderModule,
    NxCopytextModule,
    FileUploaderComponent,
    TranslateModule,
    DocumentListComponent,
  ],
})
export class ReputationalRiskUpdateModalComponent extends DocumentHandler implements OnInit {
  readonly data = inject<{
    submit: (submitParams: UpdateModalSubmitParams) => Promise<void>;
    closeModal: () => void;
    escalationContact?: string;
    hasEscalationEmailInput?: boolean;
    setShouldClose: (val: boolean) => void;
  }>(NX_MODAL_DATA);

  private readonly fb = inject(FormBuilder);
  private readonly dialogService = inject(NxDialogService);

  private readonly cveService = inject(CveService);
  private readonly userService = inject(UserService);
  private readonly documentService = inject(DocumentService);

  private iterationId?: string;

  protected step: 'quiz' | 'risk' = 'quiz';
  protected isSaving = false;
  protected savingError = false;
  protected documentIds?: string[];

  readonly quizForm = this.fb.nonNullable.group({
    controversial: ['', Validators.required],
    alert: ['', Validators.required],
    search: ['', Validators.required],
    guideline: ['', Validators.required],
  });
  readonly riskForm = this.fb.nonNullable.group({
    averageRiskScore: ['', Validators.required],
    email: [''],
    comment: [''],
  });

  ngOnInit(): void {
    const data = this.data;
    this.riskForm.patchValue({ email: data.hasEscalationEmailInput ? data.escalationContact : undefined });

    this.cveService.iterationData$.subscribe((iteration) => {
      if (!iteration) return;
      this.iterationId = iteration.iterationId;
      const savedData = iteration.customProperties.reputationalRiskData;
      if (savedData) {
        this.quizForm.patchValue(savedData.quizFormValue);
        this.riskForm.patchValue(savedData.riskFormValue);
        this.documentIds = savedData.documentIds;
        this.step = 'risk';
      }
    });
  }

  onRiskScoreChange(value: string) {
    if (+value >= MIN_SCORE_TO_ESCALATE && this.data.hasEscalationEmailInput) {
      this.riskForm.controls['email'].setValidators([Validators.required]);
    } else {
      this.riskForm.controls['email'].clearValidators();
    }
    this.riskForm.controls['email'].updateValueAndValidity();
  }

  get atLeastOneYes() {
    return Object.values(this.quizForm.value).includes('yes');
  }

  get isEscalationRequired() {
    if (!this.atLeastOneYes || !this.riskForm.value.averageRiskScore) return false;
    return +this.riskForm.value.averageRiskScore >= MIN_SCORE_TO_ESCALATE;
  }

  async next() {
    if (this.step === 'quiz' && this.atLeastOneYes) {
      this.step = 'risk';
    } else {
      await this.showConfirmationThenSubmit();
    }
  }

  previous() {
    if (this.step === 'risk') this.step = 'quiz';
  }

  async save() {
    this.isSaving = true;
    const buCode = this.userService.buCodeSignal();
    if (!this.iterationId) return;
    if (!buCode) return;

    try {
      this.savingError = false;
      const uploadedDocumentIds = await this.documentService.createAndUploadDocumentsIfAny(
        this.iterationId,
        buCode,
        this.documents,
      );
      await this.cveService.attachReputationalRiskDataToIteration(this.iterationId, {
        quizFormValue: this.quizForm.value as ReputationalRiskData['quizFormValue'],
        riskFormValue: this.riskForm.value as ReputationalRiskData['riskFormValue'],
        documentIds: [...(this.documentIds ?? []), ...(uploadedDocumentIds ?? [])],
      });
      this.data.closeModal();
    } catch {
      this.savingError = true;
    } finally {
      this.isSaving = false;
    }
  }

  private async showConfirmationThenSubmit() {
    const i18nOnSuccessValidate = 'custom-update.confirmation.update-sent';
    const i18nOnSuccessEscalation = 'custom-update.confirmation.escalate-sent';
    const i18nEscalation = 'custom-update.confirmation.escalate';
    const i18nValidate = 'custom-update.confirmation.update';
    const isValidate = !this.isEscalationRequired;
    let shouldClose = true;
    function setShouldClose(newVal: boolean) {
      shouldClose = newVal;
    }
    const componentDialogConfirmationRef = this.dialogService.open(ConfirmationModalComponent, {
      shouldClose: () => shouldClose,
      showCloseIcon: true,
      data: {
        setShouldClose,
        closeModal: () => {
          componentDialogConfirmationRef?.close();
        },
        i18nOnSuccess: isValidate ? i18nOnSuccessValidate : i18nOnSuccessEscalation,
        i18n: isValidate ? i18nValidate : i18nEscalation,
        bypassSuccess: true,
        submit: async () => {
          try {
            await this.submit(isValidate);
            this.data.closeModal();
          } catch (err) {
            displayError({
              dialogService: this.dialogService,
              err,
              modalsToClose: [componentDialogConfirmationRef],
            });
          }
        },
      },
    });
  }

  private async submit(isValidate: boolean) {
    if (this.isSaving) return;
    const { averageRiskScore, email, comment } = this.riskForm.value;
    this.data.setShouldClose(false);
    await this.data.submit({
      form: this.atLeastOneYes ? { ...this.quizForm.value, averageRiskScore } : this.quizForm.value,
      email,
      comment,
      documents: this.documents,
      documentIds: this.documentIds,
      isValidate,
    });
    this.data.setShouldClose(true);
  }

  get todaysDate() {
    return formatDate(new Date().toString());
  }

  get isNextDisabled() {
    const awaitedDocumentsLength = this.isEscalationRequired ? 2 : 1;
    if (this.step === 'risk') {
      const documentsCount = (this.documents?.length ?? 0) + (this.documentIds?.length ?? 0);
      return this.riskForm.invalid || documentsCount < awaitedDocumentsLength;
    }
    return this.quizForm.invalid;
  }

  get REP_RISK_SHARE_POINT_LINK() {
    return REP_RISK_SHARE_POINT_LINK;
  }
}
